// src/components/tokenPage/derivatives/OISummaryCard.tsx

import React, { useMemo } from "react";
import { MetricBox } from "../shared/TokenDashboardComponents";
import { useTokenDerivatives, useTokenFundamentals } from "@/contexts/token";
import { DataCard } from "@/components/ui";

interface Props {
  ticker: string;
}

const OISummaryCard: React.FC<Props> = () => {
  const { openInterest, isLoading: derivativesLoading } = useTokenDerivatives();
  const { snapshot, isLoading: fundamentalsLoading } = useTokenFundamentals();
  
  const marketCap = snapshot.marketCap;
  const volume = snapshot.volume;
  
  // Compute ratios that need external data (market cap, volume)
  const { mcapToOi, oiToVolume } = useMemo(() => {
    const oiNow = openInterest.latest;
    const mcapToOiValue = oiNow != null && marketCap != null && oiNow > 0 ? marketCap / oiNow : null;
    const toVol = oiNow != null && volume != null && volume > 0 ? oiNow / volume : null;
    return { mcapToOi: mcapToOiValue, oiToVolume: toVol };
  }, [openInterest.latest, marketCap, volume]);
  
  const isLoading = derivativesLoading || fundamentalsLoading;

  // All OI changes come pre-computed from context
  const { latest: oiNow, change1dPct, change7dPct, change30dPct, change365dPct } = openInterest;

  // Hide if no data available (after loading)
  const hasData = oiNow != null || change1dPct != null || change7dPct != null || 
                  change30dPct != null || change365dPct != null || mcapToOi != null || oiToVolume != null;
  if (!isLoading && !hasData) {
    return null;
  }

  return (
    <DataCard
      title="Open Interest"
      subtitle="Snapshot, change and ratios"
      icon="line-chart"
      iconColor="text-fuchsia-400"
      isLoading={isLoading}
      loadingVariant="stats"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        <MetricBox label="Open Interest (USD)" value={oiNow} fmt="currency" />
        <MetricBox label="1d change" value={change1dPct} fmt="pct1" toneBySign />
        <MetricBox label="7d change" value={change7dPct} fmt="pct1" toneBySign />
        <MetricBox label="30d change" value={change30dPct} fmt="pct1" toneBySign />
        <MetricBox label="365d change" value={change365dPct} fmt="pct1" toneBySign />
        <MetricBox label="Market Cap / OI" value={mcapToOi} fmt="ratio4" suffix="x" />
        <MetricBox label="OI / Volume" value={oiToVolume} fmt="ratio4" suffix="x" />
      </div>
    </DataCard>
  );
};

export default OISummaryCard;
